import { unwrap, type Schemas } from '@swarm/api-client';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export type Task = Schemas['TaskDto'];
export type TaskView = 'today' | 'upcoming' | 'all' | 'mine';
export type SaveTaskRequest = Schemas['SaveTaskRequest'];
export type Reminder = Schemas['ReminderDto'];
export type RecurrenceUnit = Schemas['RecurrenceUnitDto'];
export type NotificationPreferences = Schemas['NotificationPreferencesDto'];
export type NotificationSetting = Schemas['NotificationSettingDto'];

export interface TaskFilter {
  view?: TaskView | undefined;
  assignee_id?: string | undefined;
  location_id?: string | undefined;
  colony_id?: string | undefined;
  customer_id?: string | undefined;
  sale_id?: string | undefined;
  include_done?: boolean | undefined;
}

const keys = {
  all: ['tasks'] as const,
  list: (f: TaskFilter) => ['tasks', 'list', f] as const,
  task: (id: string) => ['tasks', 'task', id] as const,
  assignees: ['tasks', 'assignees'] as const,
  preferences: ['me', 'notifications'] as const,
};

/** Tasks also show in the colony timeline. */
function invalidate(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.all }),
    client.invalidateQueries({ queryKey: ['apiary'] }),
  ]);
}

const ifMatch = (version?: number): Record<string, string> =>
  version === undefined ? {} : { 'If-Match': `"${version}"` };

export function useTasks(filter: TaskFilter = {}, enabled = true) {
  return useQuery({
    queryKey: keys.list(filter),
    queryFn: async () =>
      unwrap(
        await api.GET('/tasks', {
          params: {
            query: {
              view: filter.view ?? 'today',
              ...(filter.assignee_id ? { assignee_id: filter.assignee_id } : {}),
              ...(filter.location_id ? { location_id: filter.location_id } : {}),
              ...(filter.colony_id ? { colony_id: filter.colony_id } : {}),
              ...(filter.customer_id ? { customer_id: filter.customer_id } : {}),
              ...(filter.sale_id ? { sale_id: filter.sale_id } : {}),
              include_done: filter.include_done ?? false,
            },
          },
        }),
      ),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: keys.task(id),
    queryFn: async () =>
      unwrap(await api.GET('/tasks/{task_id}', { params: { path: { task_id: id } } })),
  });
}

export function useAssignees(enabled = true) {
  return useQuery({
    queryKey: keys.assignees,
    queryFn: async () => unwrap(await api.GET('/task-assignees')),
    enabled,
    staleTime: 60_000,
  });
}

export function useSaveTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      version,
    }: {
      id?: string | undefined;
      body: SaveTaskRequest;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/tasks/{task_id}', {
              params: { path: { task_id: id } },
              body,
              headers: ifMatch(version),
            }),
          )
        : unwrap(await api.POST('/tasks', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useTaskAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: string;
      action: 'complete' | 'reopen' | 'delete';
    }) => {
      const path = { params: { path: { task_id: id } } };
      switch (action) {
        case 'complete':
          return unwrap(await api.POST('/tasks/{task_id}/complete', path));
        case 'reopen':
          return unwrap(await api.POST('/tasks/{task_id}/reopen', path));
        case 'delete':
          return unwrap(await api.DELETE('/tasks/{task_id}', path));
      }
    },
    onSuccess: () => invalidate(client),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: keys.preferences,
    queryFn: async () => unwrap(await api.GET('/me/notifications')),
  });
}

export function useSaveNotificationPreferences() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      enabled,
      digest_time,
      version,
    }: {
      enabled: NotificationSetting[];
      digest_time: string;
      version: number;
    }) =>
      unwrap(
        await api.PUT('/me/notifications', {
          body: { enabled, digest_time },
          headers: ifMatch(version),
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.preferences }),
  });
}
